import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="stripe-payment"
export default class extends Controller {
  static targets = [
    "loading",
    "error",
    "errorMessage",
    "submitButton",
    "buttonText",
    "buttonSpinner"
  ]

  static values = {
    publishableKey: String,
    clientSecret: String,
    returnUrl: String
  }

  async connect() {
    // Load Stripe.js if not already loaded
    if (!window.Stripe) {
      await this.loadStripeJs()
    }

    this.stripe = window.Stripe(this.publishableKeyValue)
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: "#059669", // emerald-600
          colorBackground: "#ffffff",
          colorText: "#1e293b", // slate-800
          colorDanger: "#dc2626",
          fontFamily: "system-ui, sans-serif",
          spacingUnit: "4px",
          borderRadius: "8px"
        }
      }
    })

    // Create and mount the Payment Element
    this.paymentElement = this.elements.create("payment", {
      layout: "tabs"
    })

    this.paymentElement.mount("#payment-element")

    // Hide loading state when ready
    this.paymentElement.on("ready", () => {
      if (this.hasLoadingTarget) {
        this.loadingTarget.classList.add("hidden")
      }
    })

    // Handle validation errors
    this.paymentElement.on("change", (event) => {
      if (event.error) {
        this.showError(event.error.message)
      } else {
        this.hideError()
      }
    })
  }

  async loadStripeJs() {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script")
      script.src = "https://js.stripe.com/v3/"
      script.async = true
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  async submit() {
    this.setLoading(true)

    const { error } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: this.returnUrlValue
      }
    })

    // If there's an error, the user stays on this page
    // If successful, they're redirected to return_url
    if (error) {
      // This point is reached if there's an immediate error
      // (card declined, processing error, etc.)
      if (error.type === "card_error" || error.type === "validation_error") {
        this.showError(error.message)
      } else {
        this.showError("An unexpected error occurred. Please try again.")
      }
      this.setLoading(false)
    }
  }

  showError(message) {
    if (this.hasErrorTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden")
    }
  }

  setLoading(isLoading) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = isLoading
    }
    if (this.hasButtonTextTarget && this.hasButtonSpinnerTarget) {
      if (isLoading) {
        this.buttonTextTarget.classList.add("hidden")
        this.buttonSpinnerTarget.classList.remove("hidden")
      } else {
        this.buttonTextTarget.classList.remove("hidden")
        this.buttonSpinnerTarget.classList.add("hidden")
      }
    }
  }

  disconnect() {
    if (this.paymentElement) {
      this.paymentElement.unmount()
    }
  }
}
